import { ENV } from "@/core/config/env";
import { BusinessItem } from "@/core/models/businesses/businessList";
import { slugify } from "@/lib/slugify";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { FilterSection } from "../ui/FilterSection";
import { SearchBusinessRequest } from "@/core/models/businesses/businessRequest";
import { GET_CATEGORY_BY_PARENTID } from "@/core/graphql/queries/getCategories";
import { GET_CITIES } from "@/core/graphql/queries/getCities";
import { GET_PROVINCE } from "@/core/graphql/queries/getProvinces";
import { GET_SUBURB } from "@/core/graphql/queries/getSuburbs";
import { City } from "@/core/models/locations/city";
import { Suburb } from "@/core/models/locations/suburb";
import { useQuery } from "@apollo/client";
import { GET_BUSINESS_LIST } from "@/core/graphql/queries/getBusinessList";
import { DEFAULT_PAGE_SIZE } from "@/core/constants";
import { BusinessItemCard } from "./partials/BusinessItemCard";
import { Pagination } from "../shared/Pagination";
import BusinessMapListing from "./BusinessMapListing";
import { Province } from "@/core/models/locations/province";
import { Category } from "@/core/models/categories/category";
import Loading from "../shared/Loading";
import { PaginationInfo } from "../shared/PaginationInfo";
import { NoRecordsCard } from "../ui/NoRecordsCard";
import Head from "next/head";
import { useBusinessCompare } from "@/contexts/BusinessCompareContext";
import CompareBusinessModal from "./CompareBusinessModal";

type FilterState = {
    searchText?: string;
    category?: number[];
    province?: number[];
    city?: number[];
    suburb?: number[];
    page: number;
    size?: number;
};

interface BusinessListProps {
    searchParams: {
        category?: string;
        province?: string;
        city?: string;
        suburb?: string;
        text?: string;
        page?: number;
    };
    defaultCategoryIds?: string;
    initialBusinesses: BusinessItem[];
    initialPagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
    /** When true, the Bearer auth token is omitted so the API returns cross-domain results. */
    skipAuth?: boolean;
    /** Override the default map section heading. */
    mapHeading?: string;
}
export default function BusinessList({
    searchParams,
    defaultCategoryIds,
    initialBusinesses,
    initialPagination,
    skipAuth = false,
    mapHeading,
}: BusinessListProps) {
    const [businesses, setBusinesses] = useState<BusinessItem[]>(initialBusinesses || []);
    const [pagination, setPagination] = useState(initialPagination);

    const [filters, setFilters] = useState<FilterState>({
        searchText: searchParams.text || "",
        page: searchParams.page ?? 1,
        size: DEFAULT_PAGE_SIZE,
    });
    const { compareBusinesses, clearCompare, setCompareOpen } = useBusinessCompare();

    const { data: provinceRes } = useQuery(GET_PROVINCE);
    const { data: cityRes } = useQuery(GET_CITIES);
    const { data: suburbRes } = useQuery(GET_SUBURB);
    const { data: categoryRes } = useQuery(GET_CATEGORY_BY_PARENTID, {
        variables: { id: Number(ENV.CATEGORY_ID), size: 1000 },
    });

    const provinces = provinceRes?.getProvince?.result ?? [];
    const cities = cityRes?.getCity?.result ?? [];
    const suburbs = suburbRes?.getSuburb?.result ?? [];
    const categories = categoryRes?.getMstCategoryByParentId?.result ?? [];

    const filteredCities = cities.filter((city: City) =>
        filters.province?.includes(city.provinceId)
    );

    const filteredSuburbs = suburbs.filter((suburb: Suburb) =>
        filters.city?.includes(suburb.cityId)
    );

    const variables: SearchBusinessRequest = {
        companyId: null,
        companyName: filters.searchText || null,
        franchiseeId: null,
        statusIds: '1',
        categoryIds: defaultCategoryIds || filters.category?.join(',') || String(ENV.CATEGORY_ID),
        ...(defaultCategoryIds ? {} : { domainId: Number(ENV.CATEGORY_ID) }),
        provinceIds: filters.province?.join(',') || '',
        cityIds: filters.city?.join(',') || '',
        suburbIds: filters.suburb?.join(',') || '',
        page: filters.page,
        size: filters.size ?? DEFAULT_PAGE_SIZE,
    };

    const { data, loading } = useQuery(GET_BUSINESS_LIST, {
        variables,
        fetchPolicy: "network-only",
        context: skipAuth ? { skipAuth: true } : undefined,
    });

    useEffect(() => {
        // Only run if data is available
        if (provinces.length && cities.length && suburbs.length && categories.length) {
            const newFilters: Partial<FilterState> = {
                ...filters,
            };

            if (searchParams.category) {
                const matchedCategory: Category = categories.find((c: Category) =>
                    slugify(c.categoryName) === slugify(searchParams.category!)
                );
                if (matchedCategory) {
                    newFilters.category = [Number(matchedCategory.categoryId)];
                }
            }

            if (searchParams.province) {
                const matchedProvince: Province = provinces.find((p: Province) =>
                    slugify(p.provinceName) === slugify(searchParams.province!)
                );
                if (matchedProvince) {
                    newFilters.province = [Number(matchedProvince.provinceId)];
                }
            }

            if (searchParams.city) {
                const matchedCity: City = cities.find((c: City) =>
                    slugify(c.cityName) === slugify(searchParams.city!)
                );
                if (matchedCity) {
                    newFilters.city = [matchedCity.cityId];
                }
            }

            if (searchParams.suburb) {
                const matchedSuburb: Suburb = suburbs.find((s: Suburb) =>
                    slugify(s.suburbName) === slugify(searchParams.suburb!)
                );
                if (matchedSuburb) {
                    newFilters.suburb = [matchedSuburb.suburbId];
                }
            }

            // Set updated filters and page
            setFilters({
                ...newFilters,
                page: searchParams.page ?? 1,
                searchText: searchParams.text || "",
                size: DEFAULT_PAGE_SIZE,
            });
        }
    }, [provinces, cities, suburbs, categories, searchParams]);

    useEffect(() => {
        if (data?.getBusinessList) {
            setBusinesses(data.getBusinessList.result ?? []);
            setPagination({
                count: data.getBusinessList.count ?? 0,
                currentPage: data.getBusinessList.currentPage ?? 1,
                totalPages: data.getBusinessList.totalPages ?? 0,
            });
        }
    }, [data]);

    if (loading) return <Loading />;

    const handleFilterChange = (field: keyof FilterState, value: number[]) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            page: 1,
        }));
    };

    const handleSearch = (value: string) => {
        setFilters((prev) => ({
            ...prev,
            searchText: value,
            page: 1,
        }));
    };

    const handlePageChange = (newPage: number) => {
        setFilters((prev) => ({
            ...prev,
            page: newPage,
        }));
    };

    const businessCount = pagination.count;

    // Generate comma-separated strings for selected filters
    const selectedCategories = filters.category ? categories.filter((c: Category) => filters.category!.includes(c.categoryId)).map((c: Category) => c.categoryName).join(", ") : "";
    const selectedProvinces = filters.province ? provinces.filter((p: Province) => filters.province!.includes(Number(p.provinceId))).map((p: Province) => p.provinceName).join(", ") : "";
    const selectedCities = filters.city ? cities.filter((c: City) => filters.city!.includes(c.cityId)).map((c: City) => c.cityName).join(", ") : "";
    const selectedSuburbs = filters.suburb ? suburbs.filter((s: Suburb) => filters.suburb!.includes(s.suburbId)).map((s: Suburb) => s.suburbName).join(", ") : "";

    let finalLocation = "South Africa";

    if (filters.suburb != undefined && filters.suburb?.length > 0) {
        finalLocation = selectedSuburbs;
    } else if (filters.city != undefined &&filters.city?.length > 0) {
        finalLocation = selectedCities;
    } else if (filters.province != undefined &&filters.province?.length > 0) {
        finalLocation = selectedProvinces;
    }
const countText = businessCount > 0 ? `${businessCount} ` : "";

// Determine if targeting scrapyard content
const isScrapyard = filters.searchText && filters.searchText.toLowerCase().includes('scrapyard');

// SEO-optimized keywords
const baseKeyword = isScrapyard ? 'Scrapyards' : 'Car Spare Parts';
const businessTypeKeyword = isScrapyard ? 'scrapyards and salvage yards' : 'spare shops';

// Dynamic category text
const titleCategoryText = filters.category ? selectedCategories : (isScrapyard ? 'Scrapyards' : 'Spare Shops');
const descCategoryText = filters.category ? selectedCategories : (isScrapyard ? 'Scrapyards' : 'Spare Shops');

const website = "www.CarSparesEzyFind.co.za";
const startRecord = (pagination.currentPage - 1) * DEFAULT_PAGE_SIZE + 1;
const endRecord = Math.min(pagination.currentPage * DEFAULT_PAGE_SIZE, pagination.count);

// SEO-Optimized Title Tag
const title = filters.searchText && filters.searchText.trim() !== ""
    ? `${filters.searchText} Near Me in ${finalLocation} | Find Local ${titleCategoryText} | ${website}`
    : finalLocation !== "South Africa"
    ? `${baseKeyword} Near Me in ${finalLocation} | Local ${businessTypeKeyword} | ${website}`
    : `${baseKeyword} Near Me | Find Local ${businessTypeKeyword} in South Africa | ${website}`;

// SEO-Optimized Meta Description
const description = filters.searchText && filters.searchText.trim() !== ""
    ? `Looking for ${filters.searchText} near you? Find trusted ${filters.searchText} businesses in ${finalLocation}. Search by city or suburb. Browse ${countText} listings with contact details and locations.`
    : isScrapyard 
    ? `Looking for scrapyards near you? Find trusted scrapyards, salvage yards and auto dismantlers across ${finalLocation}. Search by city or suburb. Quality used parts from reputable dealers.`
    : `Looking for car spare parts near you? Find trusted local spare shops, auto parts stores and suppliers across ${finalLocation}. Search by city or suburb now.`;

// SEO-Optimized Keywords
const keywords = filters.searchText && filters.searchText.trim() !== ""
    ? `${filters.searchText} near me,${filters.searchText} in ${finalLocation},${filters.searchText} suppliers,${filters.searchText} stores,local ${filters.searchText},find ${filters.searchText},auto parts,car parts`
    : isScrapyard
    ? `scrapyards near me,scrapyards in ${finalLocation},salvage yards,auto dismantlers,used car parts,recycled auto parts,car wreckers,vehicle scrap,second hand car parts,${finalLocation} scrapyards`
    : `car spare parts near me,spare parts in ${finalLocation},auto parts near me,car parts stores,local spare shops,find spare parts,car parts suppliers,${finalLocation} auto parts,spare shops near me,automotive parts`;  //https://www.carsparesezyfind.co.za/car-spare-parts-near-me?text=?&province=selectedProvinces&city=selectedCities&suburb=selectedSuburbs&category=selectedCategories&page=filters.page
    //https://www.carsparesezyfind.co.za/car-spare-parts-near-me?text=?&province=gauteng&city=ekurhuleni&suburb=actonville&category=mechanical-parts
    //const canonicalUrl = `${ENV.DOMAIN_URL}/car-spare-parts-near-me`;

    // Change canonical URL to make it more unique with filters
        let canonicalUrl = `${ENV.DOMAIN_URL}/car-spare-parts-near-me`;

        // Build query parameters based on filter values
        const queryParams = new URLSearchParams();

        if (filters.searchText) {
            queryParams.append('text', filters.searchText);
        }

        if (filters.province && filters.province.length > 0) {
            const provinceNames = provinces
                .filter((p: Province) => filters.province!.includes(Number(p.provinceId)))
                .map((p: Province) => slugify(p.provinceName))
                .join(',');
            queryParams.append('province', provinceNames);
        }

        if (filters.city && filters.city.length > 0) {
            const cityNames = cities
                .filter((c: City) => filters.city!.includes(c.cityId))
                .map((c: City) => slugify(c.cityName))
                .join(',');
            queryParams.append('city', cityNames);
        }

        if (filters.suburb && filters.suburb.length > 0) {
            const suburbNames = suburbs
                .filter((s: Suburb) => filters.suburb!.includes(s.suburbId))
                .map((s: Suburb) => slugify(s.suburbName))
                .join(',');
            queryParams.append('suburb', suburbNames);
        }

        if (filters.category && filters.category.length > 0) {
            const categoryNames = categories
                .filter((c: Category) => filters.category!.includes(c.categoryId))
                .map((c: Category) => slugify(c.categoryName))
                .join(',');
            queryParams.append('category', categoryNames);
        }

        if (filters.page && filters.page > 1) {
            queryParams.append('page', filters.page.toString());
        }

        // Add query string to canonical URL if any filters are applied
        if (queryParams.toString()) {
            canonicalUrl += `?${queryParams.toString()}`;
            }
    
    const generatePaginationUrl = (page: number): string => {
    const queryParams = new URLSearchParams();

    if (filters.searchText) {
        queryParams.append('text', filters.searchText);
    }

    if (filters.province && filters.province.length > 0) {
        const provinceNames = provinces
            .filter((p: Province) => filters.province!.includes(Number(p.provinceId)))
            .map((p: Province) => slugify(p.provinceName))
            .join(',');
        queryParams.append('province', provinceNames);
    }

    if (filters.city && filters.city.length > 0) {
        const cityNames = cities
            .filter((c: City) => filters.city!.includes(c.cityId))
            .map((c: City) => slugify(c.cityName))
            .join(',');
        queryParams.append('city', cityNames);
    }

    if (filters.suburb && filters.suburb.length > 0) {
        const suburbNames = suburbs
            .filter((s: Suburb) => filters.suburb!.includes(s.suburbId))
            .map((s: Suburb) => slugify(s.suburbName))
            .join(',');
        queryParams.append('suburb', suburbNames);
    }

    if (filters.category && filters.category.length > 0) {
        const categoryNames = categories
            .filter((c: Category) => filters.category!.includes(c.categoryId))
            .map((c: Category) => slugify(c.categoryName))
            .join(',');
        queryParams.append('category', categoryNames);
    }

    if (page > 1) {
        queryParams.append('page', page.toString());
    }

    const queryString = queryParams.toString();
    return `${ENV.DOMAIN_URL}/car-spare-parts-near-me${queryString ? `?${queryString}` : ''}`;
    };
    
const generateMapH1Text = () => {
    if (mapHeading) return mapHeading;
    const businessTypePlural = isScrapyard ? 'scrapyards' : 'spare shops';
    
    let h1Text = `List of ${businessTypePlural} near me with map pin drop within 20KM radius`;
    
    // If search text is provided
    if (filters.searchText && filters.searchText.trim() !== "") {
        h1Text = `${filters.searchText} near me - List of ${businessTypePlural} in your area with map pin drop within 20KM radius`;
    }
    // If category is selected (and no search text)
    else if (filters.category && filters.category.length > 0) {
        h1Text = `${titleCategoryText} near me - List of ${titleCategoryText.toLowerCase()} in your area with map pin drop within 20KM radius`;
    }
    // If location is selected (and not default "South Africa")
    else if (finalLocation !== "South Africa") {
        h1Text = `${isScrapyard ? 'Scrapyards' : 'Spare shops'} near me in ${finalLocation} - List of ${businessTypePlural} in ${finalLocation} with map pin drop within 20KM radius`;
    }
    
    return h1Text;
};

    // Generate Structured Data for SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `${isScrapyard ? 'Scrapyards' : 'Car Spare Parts'} Near Me in ${finalLocation}`,
        "description": description,
        "numberOfItems": pagination.count,
        "itemListElement": businesses.slice(0, 10).map((business, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "AutoPartsStore",
                "name": business.companyName,
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": business.compStreetAddress || "",
                    "addressLocality": business.compCityName || "",
                    "addressRegion": business.compProvinceName || "",
                    "addressCountry": "ZA"
                },
                "areaServed": finalLocation
            }
        }))
    };

    const faqStructuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": `How do I find ${isScrapyard ? 'scrapyards' : 'car spare parts'} near me?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Use our location filters to search by province, city or suburb. You can also enable location detection to find businesses closest to you."
                }
            },
            {
                "@type": "Question",
                "name": `Do ${isScrapyard ? 'scrapyards' : 'spare shops'} sell used and new parts?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": isScrapyard 
                        ? "Most scrapyards specialize in quality used and recycled parts from dismantled vehicles. Some also offer reconditioned components with warranties."
                        : "Yes, most spare shops offer both OEM (Original Equipment Manufacturer) and aftermarket parts in new and used condition."
                }
            },
            {
                "@type": "Question",
                "name": `Can I contact ${isScrapyard ? 'scrapyards' : 'local spare shops'} directly?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, each listing includes contact details, phone numbers, and location information so you can reach out directly."
                }
            }
        ]
    };

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
                />
            </Head>
            <div className="flex flex-wrap lg:gap-6 lg:flex-nowrap my-10">

                {/* Left Filter Panel */}
                <div className="basis-12/12 lg:basis-4/12 xl:basis-3/12">
                    <div className="py-3 px-5 text-white bg-secondary flex justify-between items-center">
                        <p className="text-lg">Filter Option</p>
                    </div>
                    <div className="border border-gray-300">
                        {/* Search */}
                        <div className="border-b border-gray-300">
                            <div className="bg-white px-4 py-3 flex justify-between items-center cursor-pointer">
                                <p className="text-md font-semibold">Search Company Name</p>
                                <FontAwesomeIcon icon={faAngleDown} className="text-lg" />
                            </div>
                            <div className="bg-gray-50 px-4 py-5">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const input = e.currentTarget.elements.namedItem("search") as HTMLInputElement;
                                        handleSearch(input.value);
                                    }}
                                >
                                    <div className="relative">
                                        <input
                                            type="search"
                                            name="search"
                                            placeholder="Enter Company name"
                                            defaultValue={filters.searchText}
                                            className="form-control border border-gray-300 text-sm w-full h-10 px-5"
                                        />
                                        <button
                                            type="submit"
                                            className="absolute top-[50%] translate-y-[-50%] right-0 h-10 px-4 bg-primary text-white"
                                        >
                                            Search
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Filters */}
                        {!defaultCategoryIds && (
                        <FilterSection
                            title="Categories"
                            data={categories}
                            selected={filters.category}
                            onChange={(values) => handleFilterChange("category", values)}
                            labelKey="categoryName"
                            valueKey="categoryId"
                        />
                        )}
                        <FilterSection
                            title="Provinces"
                            data={provinces}
                            selected={filters.province}
                            onChange={(values) => {
                                handleFilterChange("province", values)
                                handleFilterChange("city", [])
                                handleFilterChange("suburb", [])
                            }}
                            labelKey="provinceName"
                            valueKey="provinceId"
                        />
                        {filters.province && filters.province?.length > 0 && (
                            <FilterSection
                                title="Cities"
                                data={filteredCities}
                                selected={filters.city}
                                onChange={(values) => {
                                    handleFilterChange("city", values)
                                    handleFilterChange("suburb", [])
                                }}
                                labelKey="cityName"
                                valueKey="cityId"
                            />
                        )}
                        {filters.city && filters.city?.length > 0 && (
                            <FilterSection
                                title="Suburbs"
                                data={filteredSuburbs}
                                selected={filters.suburb}
                                onChange={(values) => handleFilterChange("suburb", values)}
                                labelKey="suburbName"
                                valueKey="suburbId"
                            />
                        )}
                    </div>
                </div>

                {/* Right Business Listing */}
                <div className="basis-12/12 lg:basis-8/12 xl:basis-9/12 mt-5 lg:mt-0">
                    {pagination && (
                        <PaginationInfo
                            currentPage={pagination.currentPage}
                            pageSize={DEFAULT_PAGE_SIZE}
                            totalCount={pagination.count}
                            label={`${titleCategoryText} Businesses in ${finalLocation}`}
                        />
                    )}

                    {/* Business Cards */}
                    {businesses.length === 0 && (<NoRecordsCard />)}
                    <div className="flex flex-wrap gap-y-5 -mx-[12px]">
                        {businesses.map((business, index) => (
                            <BusinessItemCard key={`${business.companyId}-${index}`} business={business} />
                        ))}
                    </div>

                    {/* Map & Pagination */}
                    <h1 className="text-2xl font-bold mt-10 mb-5">{generateMapH1Text()}</h1>
                    <BusinessMapListing businesses={businesses} />


                    {/* Pagination */}
                    <div className="flex flex-col md:flex-row mt-10 justify-center">
                    {pagination && (
                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        generateUrl={generatePaginationUrl}
                    />
                    )}
                    </div>
                </div>
            </div>

            {compareBusinesses.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-[1000] bg-gradient-to-r from-red-600 to-red-700 shadow-2xl">
                    <div className="container py-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap items-center gap-4">
                                <p className="text-white font-semibold text-lg">
                                    Compare Businesses: {compareBusinesses.length}/2
                                </p>
                                <div className="hidden md:flex gap-2">
                                    {compareBusinesses.map((business) => (
                                        <div
                                            key={business.companyId}
                                            className="bg-white/20 backdrop-blur-sm rounded px-3 py-1 text-white text-sm"
                                        >
                                            {business.companyName.substring(0, 30)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCompareOpen(true)}
                                    disabled={compareBusinesses.length < 2}
                                    className="px-6 py-2 bg-white text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Compare Now
                                </button>
                                <button
                                    onClick={clearCompare}
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <CompareBusinessModal />
        </>

    );
}